import { useEffect, useCallback } from 'react';
import logger from '../services/loggerService';
import sessionBroadcastService from '../services/SessionBroadcastService';

export const useSessionSync = (currentUserId: string | undefined, onSessionTerminated: () => void) => {
  /**
   * Triggers a global session termination broadcast.
   * Utilizes the centralized SessionBroadcastService for consistency and security.
   */
  const broadcastTermination = useCallback(() => {
    if (!currentUserId) {
        logger.warn('Cannot broadcast session termination without an active user ID.', { context: 'useSessionSync' });
        return;
    }
    sessionBroadcastService.broadcastTermination(currentUserId);
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) return;

    /**
     * Subscribe to incoming session termination signals from other tabs.
     * The service handles forensic validation (anti-replay/DoS check) internally.
     */
    const unsubscribe = sessionBroadcastService.subscribe((event) => {
       // Only react if the signal matches the current user context
       if (event.userId === currentUserId) {
           logger.info('Intercepted verified cross-tab logout signal for current user. Tearing down...', { context: 'useSessionSync' });
           onSessionTerminated();
       } else {
           logger.debug('Ignoring cross-tab logout signal for different user context.', { context: 'useSessionSync' });
       }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUserId, onSessionTerminated]);

  return { broadcastTermination };
};
